import { useCallback, useEffect, useRef, useState } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { calcMaxDate, calcMinDate, CalendarInput, CalendarInputProps } from './CalendarInput';
import TextInput, { TextInputProps } from './TextInput';
import moment, { Moment } from 'moment';

interface DateInputProps extends TextInputProps {
	autoOk?: boolean;
	dateFormat?: string;
	parseFormats?: string[];
	calendarProps?: Omit<CalendarInputProps, 'value' | 'onChange' | 'onClose'>;
}

const DateInput = ({
	autoOk = true,
	dateFormat = 'DD/MM/YYYY',
	parseFormats,
	calendarProps,

	value, onChange, onBlur,
	disabled, readOnly,
	...textInputProps
}: DateInputProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [textValue, setTextValue] = useState('');
	const overlayPanelRef = useRef<OverlayPanel>(null);

	const maxDate = calcMaxDate({
		maxDate: calendarProps?.maxDate,
		maxYear: calendarProps?.maxYear
	});
	const minDate = calcMinDate({
		maxDate,
		minDate: calendarProps?.minDate,
		minYear: calendarProps?.minYear
	});

	const formats = parseFormats || [
		dateFormat,
		'DD/MM/YYYY', 'YYYY/MM/DD',
		'DD-MM-YYYY', 'YYYY-MM-DD',
		'DD.MM.YYYY', 'YYYY.MM.DD',
		'D/M/YYYY', 'D-M-YYYY', 'D.M.YYYY',
	];

	useEffect(() => {
		setTextValue(value ? value.format(dateFormat) : '');
	}, [value, dateFormat]);

	const handleTextChange = (text: string) => {
		setTextValue(text);

		const parsed = moment(text, formats, true);
		if (parsed.isValid()) {
			if (parsed.isBefore(minDate) || parsed.isAfter(maxDate)) return;
			onChange?.(parsed);
		}
	};

	const handleTextBlur = () => {
		if (textValue && value) {
			setTextValue(value.format(dateFormat));
		} else if (!textValue) {
			onChange?.(undefined);
		} else {
			setTextValue(value ? value.format(dateFormat) : '');
		}
	};

	const handleCalendarChange = (date: Moment | undefined) => {
		if (!date) {
			handleClear();
			return;
		}

		setTextValue(date.format(dateFormat));
		onChange?.(date);

		if (autoOk) handleClose();
	};

	const handleClear = useCallback(() => {
		setTextValue('');
		onChange?.(undefined);
	}, [onChange]);

	const handleClose = () => overlayPanelRef.current?.hide()
	const handleOpen = (e: React.MouseEvent) => {
		if (disabled || readOnly) return;
		overlayPanelRef.current?.show(e, undefined);
	};

	const handleClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		overlayPanelRef.current?.isVisible()
			? handleClose()
			: handleOpen(e);
	};


	return (
		<>
			<TextInput
				{...textInputProps}
				value={textValue}
				onClick={handleClick}
				onChange={handleTextChange}
				onBlur={handleTextBlur}
				label={textInputProps.label ?? 'Select Date'}
				placeholder={textInputProps.placeholder ?? dateFormat.toLowerCase()}
				disabled={disabled}
				readOnly={readOnly}
				isFocused={isOpen}
			/>
			<OverlayPanel
				ref={overlayPanelRef}
				onShow={() => setIsOpen(true)}
				onHide={() => {
					setIsOpen(false);
					onBlur?.();
				}}
				className="m-0 p-0 bg-transparent border-none shadow-none"
			>
				<CalendarInput
					{...calendarProps}
					value={value}
					onChange={handleCalendarChange}
					onClose={handleClose}
				/>
			</OverlayPanel>
		</>
	);
};

export default DateInput;
